import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Play } from "lucide-react";
import { useLang } from "../contexts/LanguageContext";
import { fetchArtworks } from "../lib/api";
import { Reveal, FadeIn } from "../components/site/Reveal";

/* ---------------- IMAGE / VIDEO SLOTS ----------------
 * Podmień src na właściwe pliki.
 *   PARIS_SRC       → obraz „Ukłon dla Paryża" (poziomy, 16:9 / 21:9)
 *   PORTRAIT_SRC    → portret Piotra w pracowni (pion, 3:4)
 *   COMMISSION_SRC  → obraz do sekcji „Ty masz wizję przestrzeni"
 *   REEL_X_SRC      → pionowy film 9:16 (URL pliku .mp4) lub poster (placeholder)
 * --------------------------------------------------- */
const PARIS_SRC = "https://gonzaga.pl/img/9.jpg"; // Riviera Palace — tymczasowy zamiennik
const PORTRAIT_SRC = "https://gonzaga.pl/img/pomaranczowa-sylwetka.png";
const COMMISSION_SRC = "https://gonzaga.pl/img/14.jpg";
const REELS = [
  { poster: "https://gonzaga.pl/img/8.jpg", video: null },
  { poster: "https://gonzaga.pl/img/16.jpg", video: null },
  { poster: "https://gonzaga.pl/img/atelier.png", video: null },
];

export default function Home() {
  const { t } = useLang();
  const [artworks, setArtworks] = useState([]);
  const [quoteIdx, setQuoteIdx] = useState(0);

  useEffect(() => {
    fetchArtworks().then(setArtworks).catch(() => setArtworks([]));
  }, []);

  // Auto-rotating quotes
  useEffect(() => {
    if (!t.quotes || t.quotes.length < 2) return;
    const id = setInterval(
      () => setQuoteIdx((i) => (i + 1) % t.quotes.length),
      5500
    );
    return () => clearInterval(id);
  }, [t.quotes]);

  const selected = artworks.slice(0, 4);

  return (
    <div data-testid="home-page">
      {/* ============== 1. HERO ============== */}
      <section
        className="relative pt-4 md:pt-8 pb-20 md:pb-28"
        data-testid="hero-section"
      >
        <div className="container-art">
          {/* Top image — Ukłon dla Paryża */}
          <Reveal>
            <figure
              data-testid="paris-slot"
              className="relative aspect-[21/9] md:aspect-[21/8] overflow-hidden bg-sand mb-12 md:mb-16"
            >
              <img
                src={PARIS_SRC}
                alt={t.hero.paris_caption}
                className="w-full h-full object-cover"
              />
              <figcaption className="absolute bottom-4 left-4 md:bottom-6 md:left-6 overline text-bone/95 [text-shadow:0_1px_4px_rgba(0,0,0,0.5)]">
                {t.hero.paris_caption}
              </figcaption>
            </figure>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
            <div className="md:col-span-8">
              <FadeIn>
                <div className="overline mb-8">{t.hero.eyebrow}</div>
              </FadeIn>
              <Reveal delay={0.05}>
                <h1 className="font-heading font-light leading-[0.9] tracking-tighter text-[clamp(3rem,9vw,8.5rem)]">
                  <span className="block">{t.hero.h1Line1}</span>
                  <span className="block italic text-foreground/85">
                    {t.hero.h1Line2}
                  </span>
                </h1>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="md:col-span-4">
              <p className="font-body text-base md:text-lg font-light leading-relaxed text-foreground/80 max-w-sm">
                {t.hero.lead}
              </p>
              <div className="mt-10 flex items-center gap-8">
                <Link
                  to="/galeria"
                  data-testid="hero-cta-primary"
                  className="link-underline text-[12px] uppercase tracking-[0.28em] font-medium inline-flex items-center gap-2"
                >
                  {t.hero.ctaPrimary}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
                <Link
                  to="/kontakt"
                  data-testid="hero-cta-secondary"
                  className="link-underline text-[12px] uppercase tracking-[0.28em] font-medium text-stone hover:text-ink transition-colors inline-flex items-center gap-2"
                >
                  {t.hero.ctaSecondary}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============== 2. ABOUT — portrait + text ============== */}
      <section
        className="py-24 md:py-32 border-t border-border"
        data-testid="about-block"
      >
        <div className="container-art grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-start">
          <Reveal className="md:col-span-5">
            <figure
              data-testid="portrait-slot"
              className="aspect-[3/4] overflow-hidden bg-sand relative grain"
            >
              <img
                src={PORTRAIT_SRC}
                alt="Piotr Gonzaga Myszkowski"
                className="w-full h-full object-contain object-bottom"
              />
            </figure>
            <figcaption className="overline mt-4">
              Piotr Gonzaga Myszkowski · Pracownia, Poznań
            </figcaption>
          </Reveal>

          <div className="md:col-span-7 md:pt-8">
            <Reveal>
              <div className="overline mb-6">{t.about_block.overline}</div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-balance mb-10">
                {t.about_block.title}
              </h2>
            </Reveal>
            <Reveal delay={0.08}>
              <p className="font-body text-lg font-light leading-relaxed text-foreground/85 mb-6">
                {t.about_block.p1}
              </p>
            </Reveal>
            <Reveal delay={0.16}>
              <p className="font-body text-lg font-light leading-relaxed text-foreground/85 mb-10">
                {t.about_block.p2}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <Link
                to="/o-mnie"
                data-testid="about-cta"
                className="inline-flex items-center gap-3 link-underline text-[12px] uppercase tracking-[0.28em] font-medium"
              >
                {t.about_block.cta}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============== 3. SELECTED WORKS ============== */}
      <section
        className="py-24 md:py-32 border-t border-border"
        data-testid="works-block"
      >
        <div className="container-art">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-16 md:mb-24">
              <div>
                <div className="overline mb-5">{t.works_block.overline}</div>
                <h2 className="font-heading text-4xl md:text-6xl font-light tracking-tight leading-[1.05] text-balance max-w-2xl">
                  {t.works_block.title}
                </h2>
              </div>
              <Link
                to="/galeria"
                data-testid="see-all-link"
                className="link-underline text-[11px] uppercase tracking-[0.28em] font-medium inline-flex items-center gap-2"
              >
                {t.gallery.seeAll}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-x-8 md:gap-x-12 gap-y-24 md:gap-y-40">
            {selected.map((a, i) => {
              const layouts = [
                "md:col-span-5",
                "md:col-span-6 md:col-start-7 md:mt-40",
                "md:col-span-7",
                "md:col-span-4 md:col-start-8 md:mt-24",
              ];
              const aspects = [
                "aspect-[4/5]",
                "aspect-[3/4]",
                "aspect-[5/4]",
                "aspect-[3/4]",
              ];
              return (
                <Reveal key={a.id} delay={(i % 2) * 0.08} className={layouts[i]}>
                  <Link
                    to="/galeria"
                    data-testid={`featured-artwork-${a.slug}`}
                    className="group block"
                  >
                    <div className={`${aspects[i]} overflow-hidden bg-sand`}>
                      <img
                        src={a.image}
                        alt={a.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                      />
                    </div>
                    <div className="mt-5 flex items-baseline justify-between">
                      <h3 className="font-heading text-2xl font-light tracking-tight italic">
                        {a.title}
                      </h3>
                      <span className="overline">{a.year}</span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============== 4. ROTATING QUOTES ============== */}
      <section
        className="py-32 md:py-48 bg-ink text-bone relative overflow-hidden"
        data-testid="quotes-section"
      >
        <div className="container-art min-h-[40vh] flex flex-col justify-center">
          <div className="overline text-bone/50 mb-12">— Cytat</div>

          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={quoteIdx}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -24 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`quote-${quoteIdx + 1}`}
                className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-tight leading-[1.08] italic text-balance"
              >
                „{t.quotes[quoteIdx].t}"
              </motion.blockquote>
            </AnimatePresence>

            <div className="mt-12 flex items-center gap-4">
              <span className="w-10 h-px bg-bone/40" />
              <span className="text-[11px] uppercase tracking-[0.28em] text-bone/70">
                {t.quotes[quoteIdx].a}
              </span>
            </div>

            {/* Dots */}
            <div className="mt-10 flex items-center gap-3">
              {t.quotes.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  data-testid={`quote-dot-${i + 1}`}
                  onClick={() => setQuoteIdx(i)}
                  aria-label={`Cytat ${i + 1}`}
                  className={`h-px transition-all ${
                    quoteIdx === i ? "w-12 bg-bone" : "w-6 bg-bone/30"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============== 5. COMMISSION — text left, image right ============== */}
      <section
        className="py-24 md:py-32"
        data-testid="commission-block"
      >
        <div className="container-art grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20 items-center">
          <div className="md:col-span-6">
            <Reveal>
              <div className="overline mb-6">{t.commission_block.overline}</div>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-[64px] font-light tracking-tight leading-[1.05] text-balance">
                <span className="block">{t.commission_block.h1}</span>
                <span className="block italic text-foreground/85">
                  {t.commission_block.h2}
                </span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-10 font-body text-base md:text-lg font-light leading-relaxed text-foreground/80">
                {t.commission_block.p1}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-6 font-body text-base md:text-lg font-light leading-relaxed text-foreground/80">
                {t.commission_block.p2}
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="mt-12">
                <Link
                  to="/kontakt"
                  data-testid="commission-cta"
                  className="inline-flex items-center gap-3 bg-ink text-bone px-7 py-3.5 text-[12px] uppercase tracking-[0.28em] font-medium hover:bg-ink/85 transition-colors"
                >
                  {t.commission_block.cta}
                  <ArrowUpRight size={14} strokeWidth={1.5} />
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.12} className="md:col-span-6">
            <figure
              data-testid="commission-slot"
              className="aspect-[4/5] overflow-hidden bg-sand"
            >
              <img
                src={COMMISSION_SRC}
                alt="Praca z kolekcji Atelier Gonzaga"
                className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out hover:scale-[1.03]"
              />
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ============== 6. REELS — vertical 9:16 ============== */}
      <section
        className="py-24 md:py-32 bg-sand"
        data-testid="reels-section"
      >
        <div className="container-art">
          <Reveal>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12 md:mb-16">
              <div>
                <div className="overline mb-5">{t.reels.overline}</div>
                <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05] text-balance max-w-2xl">
                  {t.reels.title}
                </h2>
              </div>
              <p className="max-w-sm text-base font-light text-foreground/75 leading-relaxed">
                {t.reels.lead}
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {t.reels.items.map((item, i) => (
              <Reveal key={item.t} delay={i * 0.1}>
                <article
                  data-testid={`reel-${i + 1}`}
                  className="group relative aspect-[9/16] overflow-hidden bg-ink cursor-pointer"
                >
                  {REELS[i]?.video ? (
                    <video
                      data-testid={`reel-video-${i + 1}`}
                      className="w-full h-full object-cover"
                      autoPlay
                      muted
                      loop
                      playsInline
                      poster={REELS[i].poster}
                    >
                      <source src={REELS[i].video} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={REELS[i]?.poster}
                      alt={item.t}
                      className="w-full h-full object-cover opacity-90 transition-transform duration-[1400ms] ease-out group-hover:scale-[1.04]"
                    />
                  )}

                  {/* Play badge */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full border border-bone/70 backdrop-blur-md bg-ink/20 flex items-center justify-center transition-transform duration-500 group-hover:scale-110">
                      <Play
                        size={22}
                        strokeWidth={1.4}
                        className="text-bone translate-x-[1px]"
                        fill="currentColor"
                      />
                    </div>
                  </div>

                  {/* Caption */}
                  <div className="absolute inset-x-0 bottom-0 p-5 md:p-6 bg-gradient-to-t from-ink/85 via-ink/30 to-transparent">
                    <div className="overline text-bone/80 mb-1">0{i + 1}</div>
                    <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight text-bone italic">
                      {item.t}
                    </h3>
                    <p className="mt-1 text-sm font-light text-bone/85">
                      {item.d}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <p className="mt-10 text-xs italic text-foreground/55 max-w-xl">
              Sloty 9:16 gotowe na filmy z pracowni — wystarczy podmienić
              <code className="mx-1 font-mono text-foreground/70">REELS[i].video</code>
              na URL pliku MP4.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============== BIG LOGO / WORDMARK ============== */}
      <section
        className="border-t border-border overflow-hidden"
        data-testid="wordmark-section"
        aria-label="Gonzaga"
      >
        <div className="container-art py-20 md:py-28">
          <Reveal>
            <div className="flex items-baseline justify-between mb-8 md:mb-12">
              <span className="overline">Atelier</span>
              <span className="overline">Est. 1998 · Poznań</span>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2
              className="font-heading font-light tracking-[-0.04em] leading-[0.82] text-ink text-[clamp(5rem,22vw,22rem)]"
              data-testid="wordmark-logo"
            >
              Gonzaga
            </h2>
          </Reveal>

          {/* Minimalizm — odręczna sygnatura */}
          <Reveal delay={0.18}>
            <div className="mt-10 md:mt-14 flex items-center gap-6">
              <span className="hidden md:block flex-1 h-px bg-border" />
              <img
                src="https://customer-assets.emergentagent.com/job_gonzaga-platform/artifacts/lz7u4cl1_image.png"
                alt="minimalizm — sygnatura"
                data-testid="minimalism-signature"
                className="h-16 md:h-24 lg:h-32 w-auto object-contain select-none pointer-events-none"
                draggable="false"
                style={{ mixBlendMode: "multiply" }}
              />
              <span className="hidden md:block flex-1 h-px bg-border" />
            </div>
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mt-6 md:mt-8 flex items-baseline justify-between">
              <span className="font-heading italic text-xl md:text-3xl font-light text-foreground/70">
                Myszkowski
              </span>
              <span className="overline">— Painter</span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============== QUIET FOOTER CTA ============== */}
      <section className="py-32 md:py-48 bg-sand" data-testid="quiet-cta">
        <div className="container-art text-center">
          <Reveal>
            <p className="overline mb-10">{t.contact.overline}</p>
            <h2 className="font-heading text-5xl md:text-7xl lg:text-[96px] font-light tracking-tighter leading-[1.02] text-balance max-w-5xl mx-auto">
              <span className="italic">piotr</span>@gonzaga.pl
            </h2>
            <div className="mt-12">
              <Link
                to="/kontakt"
                data-testid="quiet-cta-link"
                className="inline-flex items-center gap-3 link-underline text-[12px] uppercase tracking-[0.28em] font-medium"
              >
                {t.common.talkAboutArt}
                <ArrowUpRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
